import { useState, useEffect } from 'react';
import { ArcGisMap } from "../../../../components/Common";
import { getPolygon, getSeparatedGeometries } from "../../../../components/Common/Map/helper";
import {TGeometry, TNullable} from "../../../../common/types/common";
import Search from "@arcgis/core/widgets/Search";
import Sketch from "@arcgis/core/widgets/Sketch";
import MapView from "@arcgis/core/views/MapView";

import "./styles.scss";

interface Props {
    searchTerm: string,
    geometry?: TGeometry,
    setGraphics: (_geometries: TGeometry[]) => void,
    onLocationChanged: (_location: string) => void,
}

const LocationMap = ({
    geometry,
    setGraphics,
    searchTerm,
    onLocationChanged,
}: Props) => {
    const [sketch, setSketch] = useState<TNullable<Sketch>>(null);
    const [view, setView] = useState<TNullable<MapView>>(null);
    const [searchWidget, setSearchWidget] = useState<TNullable<Search>>(null);
    const getAdjustedGeometry = items => items.map(({ geometry: { rings, spatialReference: { wkid } } }) => ({ rings, spatialReference: { wkid } }));

    const onMapInit = ({ view, sketch, searchWidget }: { view: MapView, sketch: Sketch, searchWidget: Search }) => {
        setSketch(sketch);
        setView(view);
        setSearchWidget(searchWidget);

        sketch.on("create", (event) => {
            if (event.state === "complete") {
                setGraphics(getAdjustedGeometry((sketch.layer.graphics as any).items));
            }
        });

        sketch.on("update", (event) => {
            if (event.state === "complete") {
                setGraphics(getAdjustedGeometry((sketch.layer.graphics as any).items));
            }
        });

        sketch.on("delete", () => {
            setGraphics(getAdjustedGeometry((sketch.layer.graphics as any).items));
        });

        searchWidget.on("select-result", ({ result: { name } }) => {
            onLocationChanged(name);
        });
    };

    useEffect(() => {
        if (view && sketch && geometry) {
            // Add existing geometries
            const geometries = getSeparatedGeometries(geometry);

            geometries.forEach((item) => {
                const polygon = getPolygon(item);
                sketch.layer.graphics.add(polygon);
            });

            setGraphics(getAdjustedGeometry((sketch.layer.graphics as any).items));

            // Center the map to show all existing geometries
            const polygon = getPolygon(geometry);
            view.when(() => { // View is ready now
                view.goTo({ center: [polygon] });
            });
        }
    }, [geometry, view, sketch]);

    useEffect(() => {
        if (searchWidget) {
            searchWidget.search(searchTerm);
        }
    }, [searchWidget, searchTerm]);

    return <ArcGisMap onInit={onMapInit} withSearch withSketch />;
};

export default LocationMap;
